import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGamerAuth } from '../GamerAuthContext';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc, increment, collection, query, where, getDocs, addDoc, onSnapshot, writeBatch } from 'firebase/firestore';

export default function TournamentResult({ tournament, onClose }) {
    useEffect(() => {
        if (window.lenis && typeof window.lenis.scrollTo === 'function') {
            window.lenis.scrollTo(0, { immediate: true });
        } else {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }

        // Add history state to handle device back button
        window.history.pushState({ resultOpen: true }, '');

        const handlePopState = (e) => {
            onClose();
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [onClose]);

    const { gamerUser } = useGamerAuth();

    const mode = tournament.mode.toUpperCase();
    const isTeamMode = mode !== 'SOLO';
    const [viewType, setViewType] = useState('INDIVIDUAL');

    // Check if user has already submitted for this specific tournament
    const hasSubmitted = typeof window !== 'undefined' && localStorage.getItem(`submitted_stats_${tournament?.id}`) === 'true';

    const uid1 = gamerUser?.profileData?.gameUid1;
    const uid2 = gamerUser?.profileData?.gameUid2;

    let registeredIgn = "GAMER";
    let registeredUid = "UNKNOWN";

    if (tournament?.bookedSlots) {
        const userSlot = tournament.bookedSlots.find(slot => slot.uid === uid1 || slot.uid === uid2);
        if (userSlot) {
            registeredIgn = userSlot.ign || "GAMER";
            registeredUid = userSlot.uid || "UNKNOWN";
        }
    }

    const [showClaimModal, setShowClaimModal] = useState(false); // !hasSubmitted
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [reportPlayer, setReportPlayer] = useState(null);
    const [reportSubmitted, setReportSubmitted] = useState(false);
    const [reportDetailsPlayer, setReportDetailsPlayer] = useState(null);
    const [selectedReasons, setSelectedReasons] = useState({ wallHack: false, autoAim: false, speedHack: false });
    const [uploadedImage, setUploadedImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [timeLeft, setTimeLeft] = useState(300); // 5 mins
    const [killsInput, setKillsInput] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [parsedJsonData, setParsedJsonData] = useState(null);
    const [isLuckyNoobFound, setIsLuckyNoobFound] = useState(false);
    const [luckyNoobPlayerUid, setLuckyNoobPlayerUid] = useState(null);
    const [showLuckySpinner, setShowLuckySpinner] = useState(false);
    const [spinnerCandidates, setSpinnerCandidates] = useState([]);
    const [currentSpinnerName, setCurrentSpinnerName] = useState("");
    const [isSpinning, setIsSpinning] = useState(false);
    const [tempLuckyNoobUid, setTempLuckyNoobUid] = useState(null);
    const [showPrizeInfoModal, setShowPrizeInfoModal] = useState(false);
    const [realReportsData, setRealReportsData] = useState([]);
    const [uploadedResult, setUploadedResult] = useState(null);
    const fileInputRef = React.useRef(null);

    const hasAlreadyReported = useMemo(() => {
        if (!gamerUser) return false;
        const currentUserId = gamerUser?.uid || gamerUser?.gamerCode;
        return realReportsData.some(r => r.uid === currentUserId);
    }, [gamerUser, realReportsData]);

    useEffect(() => {
        if (!tournament?.id) return;
        try {
            const reportsRef = collection(db, 'matchReports', String(tournament.id), 'reports');
            const q = query(reportsRef);

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const reports = [];
                snapshot.forEach(doc => {
                    reports.push({ id: doc.id, ...doc.data() });
                });
                setRealReportsData(reports);
            }, (error) => {
                console.error("Failed to listen to match reports:", error);
            });

            return () => unsubscribe();
        } catch (e) {
            console.error("Error setting up match reports listener:", e);
        }
    }, [tournament?.id]);

    useEffect(() => {
        // Timer removed
    }, [showClaimModal]);

    useEffect(() => {
        if (!isSpinning || spinnerCandidates.length === 0) return;

        const spinDuration = 3000;
        const spinInterval = 50;
        const startTime = Date.now();

        const intervalId = setInterval(() => {
            const elapsed = Date.now() - startTime;
            if (elapsed >= spinDuration) {
                clearInterval(intervalId);
                const finalLucky = spinnerCandidates[Math.floor(Math.random() * spinnerCandidates.length)];
                setCurrentSpinnerName(finalLucky.name);
                setTempLuckyNoobUid(finalLucky.uid);
                setIsSpinning(false);
            } else {
                const randomName = spinnerCandidates[Math.floor(Math.random() * spinnerCandidates.length)].name;
                setCurrentSpinnerName(randomName);
            }
        }, spinInterval);

        return () => clearInterval(intervalId);
    }, [isSpinning, spinnerCandidates]);

    const handleConfirmLuckyNoob = () => {
        if (tempLuckyNoobUid) {
            setLuckyNoobPlayerUid(tempLuckyNoobUid);
            setIsLuckyNoobFound(true);
            setShowLuckySpinner(false);
        }
    };

    const handleRechooseLuckyNoob = () => {
        setTempLuckyNoobUid(null);
        setIsSpinning(true);
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            const toBase64 = file => new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => {
                    let encoded = reader.result.toString().replace(/^data:(.*,)?/, '');
                    if ((encoded.length % 4) > 0) {
                        encoded += '='.repeat(4 - (encoded.length % 4));
                    }
                    resolve(encoded);
                };
                reader.onerror = error => reject(error);
            });

            const base64Image = await toBase64(uploadedImage);

            // Send image to backend for Gemini processing
            const response = await fetch('https://bgmiesports.gridlabsproduction.workers.dev/api/analyze-result', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    image: base64Image,
                    mimeType: uploadedImage.type || "image/jpeg"
                })
            });

            const data = await response.json();

            if (data.success) {
                const extractedData = data.data;
                const extractedKills = extractedData.players?.reduce((sum, p) => sum + (p.kills || 0), 0) || 0;

                // Validate IGN
                const nameMatch = extractedData.players?.some(p =>
                    p.username && p.username.toLowerCase() === registeredIgn.toLowerCase()
                );

                setParsedJsonData(JSON.stringify(extractedData, null, 2));
                setIsSubmitting(false);
                setShowClaimModal(false);

                if (nameMatch) {
                    setShowSuccessModal(true);
                    if (typeof window !== 'undefined') {
                        localStorage.setItem(`submitted_stats_${tournament?.id}`, 'true');
                    }
                } else {
                    setShowErrorModal(true);
                }
            } else {
                alert(`Error: ${data.error || 'Failed to process image'}`);
                setIsSubmitting(false);
            }
        } catch (error) {
            console.error("Error submitting stats:", error);
            alert("Error connecting to server. Please try again.");
            setIsSubmitting(false);
        }
    };

    // Parse results from uploaded JSON or Firebase
    const { teamResults, individualResults, perKillPrice, fixedValidKills, killPool, winnerPool, luckyNoobPool } = useMemo(() => {
        let totalSlotsStr = tournament.slots || "100 / 100";
        let filledSlots = parseInt(totalSlotsStr.split(' / ')[0]) || 100;
        if (tournament.bookedSlots && tournament.bookedSlots.length > filledSlots) {
            filledSlots = tournament.bookedSlots.length;
        }
        const totalSlots = parseInt(totalSlotsStr.split(' / ')[1]) || 100;
        const entryFeeGLPC = parseInt((tournament.entry || "₹ 0").replace(/\D/g, '')) || 0;
        const entryFeeINR = entryFeeGLPC * 1.25;

        const totalCollection = filledSlots * entryFeeINR;
        const winnerPool = totalCollection * 0.35;
        const killPool = totalCollection * 0.50;

        let slotsPerTeam = 1;
        if (mode === 'DUO') slotsPerTeam = 2;
        if (mode === 'SQUAD') slotsPerTeam = 4;

        if (tournament.resultPublished && tournament.resultsData) {
            return {
                teamResults: tournament.resultsData.teamResults || [],
                individualResults: tournament.resultsData.individualResults || [],
                perKillPrice: tournament.resultsData.perKillPrice || 0,
                fixedValidKills: tournament.resultsData.fixedValidKills || 0,
                killPool: tournament.resultsData.killPool || killPool,
                winnerPool: tournament.resultsData.winnerPool || winnerPool,
                luckyNoobPool: tournament.resultsData.luckyNoobPool || 0
            };
        }

        if (!uploadedResult) {
            return { teamResults: [], individualResults: [], perKillPrice: 0, fixedValidKills: 0, killPool, winnerPool, luckyNoobPool: 0 };
        }

        let totalKills = 0;
        const rawTeams = [];
        uploadedResult.forEach(t => {
            t.players.forEach(p => {
                totalKills += (p.kills || 0);
            });
            rawTeams.push({
                teamPosition: t.teamPosition,
                actualTeamIndex: t.teamId - 1,
                teamKills: t.players.reduce((sum, p) => sum + (p.kills || 0), 0),
                members: t.players.map((p, index) => ({
                    kills: p.kills || 0,
                    isUser: !!p.realUid || !!p.uid,
                    realName: p.realName || p.ign,
                    realUid: p.realUid || p.uid,
                    position: index + 1
                }))
            });
        });

        // Sort teams by position
        rawTeams.sort((a, b) => a.teamPosition - b.teamPosition);

        const fixedKillsCount = Math.max(1, totalKills);
        const calculatedPerKillPrice = killPool / fixedKillsCount;

        const teams = [];
        const individuals = [];
        let individualPosCounter = 1;

        // Pre-defined Tailwind text colors for teams
        const teamColors = [
            'text-blue-400', 'text-green-400', 'text-purple-400',
            'text-pink-400', 'text-cyan-400', 'text-orange-400',
            'text-indigo-400', 'text-rose-400', 'text-teal-400', 'text-fuchsia-400'
        ];

        // Phase 2: Calculate Prizes and XP
        let luckyNoobPool = 0;
        for (let i = 0; i < rawTeams.length; i++) {
            const { teamPosition, actualTeamIndex, teamKills, members } = rawTeams[i];

            let teamPrizeValue = 0;
            let teamXpValue = 0;

            const isWinningTeam = teamPosition === 1;

            // Assign color based on position (Top 3 get special colors, others get from array)
            let teamPosColor = 'text-gray-600 dark:text-gray-400';
            if (teamPosition === 1) teamPosColor = 'text-yellow-500';
            else if (teamPosition === 2) teamPosColor = 'text-gray-300';
            else if (teamPosition === 3) teamPosColor = 'text-amber-600';
            else teamPosColor = teamColors[(teamPosition - 4) % teamColors.length];

            members.forEach((member) => {
                // Kill Prize
                let indPrizeValue = 0;
                let actualKillEarnings = 0;
                // Winner Prize
                if (isWinningTeam) {
                    luckyNoobPool += member.kills * calculatedPerKillPrice;
                    indPrizeValue += (winnerPool / slotsPerTeam);
                } else {
                    actualKillEarnings = member.kills * calculatedPerKillPrice;
                    indPrizeValue += actualKillEarnings;
                }

                // XP (Survival XP: Proportional to 100 based on totalSlots + 5 XP per kill)
                const baseSurvivalXp = Math.max(1, Math.round(((totalSlots - teamPosition + 1) / totalSlots) * 100));
                const xp = baseSurvivalXp + (member.kills * 5);

                teamPrizeValue += indPrizeValue;
                teamXpValue += xp;

                const randomDummyName = "GAMER";
                const playerUid = member.realUid || `UNKNOWN`;
                const playerReports = realReportsData.filter(r => String(r.reportedUid) === String(playerUid));
                const dedupedMap = new Map();
                playerReports.forEach(r => {
                    if (dedupedMap.has(r.uid)) {
                        const existing = dedupedMap.get(r.uid);
                        const mergedReasons = Array.from(new Set([...(existing.reasons || []), ...(r.reasons || [])]));
                        dedupedMap.set(r.uid, { ...existing, reasons: mergedReasons });
                    } else {
                        dedupedMap.set(r.uid, r);
                    }
                });
                const reportedBy = Array.from(dedupedMap.values());
                const reportCount = reportedBy.length;

                const player = {
                    name: member.realName || randomDummyName,
                    uid: playerUid,
                    realUid: playerUid,
                    teamId: actualTeamIndex + 1,
                    teamPosition: teamPosition,
                    position: teamPosition,
                    posColor: teamPosColor,
                    kills: member.kills,
                    xp: `+${xp} XP`,
                    prize: indPrizeValue > 0 ? `₹ ${Number.isInteger(indPrizeValue) ? indPrizeValue : indPrizeValue.toFixed(2)}` : "-",
                    actualKillEarnings: actualKillEarnings,
                    isUser: member.isUser,
                    reports: reportCount,
                    reportedBy: reportedBy
                };
                individuals.push(player);
                individualPosCounter++;
            });

            teams.push({
                teamName: `TEAM ${actualTeamIndex + 1}`,
                position: teamPosition,
                posColor: teamPosColor,
                kills: teamKills,
                xp: `+${teamXpValue} XP`,
                prize: teamPrizeValue > 0 ? `₹ ${Number.isInteger(teamPrizeValue) ? teamPrizeValue : teamPrizeValue.toFixed(2)}` : "-",
                isUserTeam: members.some(m => m.isUser)
            });
        }

        return { teamResults: teams, individualResults: individuals, perKillPrice: calculatedPerKillPrice, fixedValidKills: fixedKillsCount, killPool, winnerPool, luckyNoobPool };
    }, [tournament, mode, uid1, uid2, realReportsData, uploadedResult]);

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const json = JSON.parse(e.target.result);
                if (Array.isArray(json)) {
                    setUploadedResult(json);
                } else {
                    alert("Invalid JSON format. Expected an array of teams.");
                }
            } catch (err) {
                alert("Invalid JSON file");
            }
        };
        reader.readAsText(file);
    };

    const [isPublishing, setIsPublishing] = useState(false);

    const handlePublishResults = async () => {
        if (!tournament?.id || tournament?.resultPublished || !individualResults || individualResults.length === 0 || !tournament.docId) return;
        if (!window.confirm("Are you sure you want to publish the results and distribute XP and Prizes to all participants?")) return;

        setIsPublishing(true);
        try {
            const batch = writeBatch(db);
            const actualPlayers = individualResults.filter(p => p.realUid);
            let updatesCount = 0;

            // Group players to prevent fetching sequentially to save time
            const userRefs = actualPlayers.map(p => {
                return {
                    player: p,
                    matchesRef: doc(db, 'users', p.realUid, 'transactions', 'matchesData'),
                    vaultRef: doc(db, 'users', p.realUid, 'transactions', 'vaultData')
                };
            });

            // Fetch all required docs in parallel
            const snapPromises = [];
            userRefs.forEach(u => {
                snapPromises.push(getDoc(u.matchesRef));
                snapPromises.push(getDoc(u.vaultRef));
            });
            const snaps = await Promise.all(snapPromises);

            for (let i = 0; i < userRefs.length; i++) {
                const { player, matchesRef, vaultRef } = userRefs[i];
                const matchesSnap = snaps[i * 2];
                const vaultSnap = snaps[i * 2 + 1];

                let combatRecords = {
                    totalMatches: 0, totalWins: 0, top10s: 0, totalKills: 0,
                    prestigeLevel: 1, currentXP: 0, lifetimeXP: 0, consecutiveWins: 0
                };
                let tournamentHistory = [];
                let bountyVault = { tournamentPayouts: 0, eliminationBonuses: 0, peakWinnings: 0, totalCashouts: 0 };

                if (matchesSnap.exists()) {
                    const data = matchesSnap.data();
                    if (data.combatRecords) combatRecords = data.combatRecords;
                    if (data.tournamentHistory) tournamentHistory = data.tournamentHistory;
                    if (data.bountyVault) bountyVault = data.bountyVault;
                }

                const userXp = parseInt(player.xp.replace(/\D/g, '')) || 0;
                let milestoneReward = 0;
                let totalPrize = 0;

                if (!tournamentHistory.find(h => h.id === tournament.id)) {
                    combatRecords.totalMatches += 1;
                    combatRecords.totalKills += player.kills;
                    if (player.teamPosition === 1) {
                        combatRecords.totalWins += 1;
                        combatRecords.consecutiveWins += 1;
                    } else {
                        combatRecords.consecutiveWins = 0;
                    }
                    if (player.teamPosition <= 10) combatRecords.top10s = (combatRecords.top10s || 0) + 1;

                    const oldLifetime = combatRecords.lifetimeXP;
                    combatRecords.lifetimeXP += userXp;
                    const lifetime = combatRecords.lifetimeXP;

                    const kd = combatRecords.totalMatches > 0 ? (combatRecords.totalKills / combatRecords.totalMatches) : 0;
                    const milestonesCrossed = Math.floor(lifetime / 100) - Math.floor(oldLifetime / 100);
                    if (kd < 1.50 && milestonesCrossed > 0) milestoneReward = milestonesCrossed * 4;

                    const L = Math.floor((1 + Math.sqrt(1 + 8 * (lifetime / 100))) / 2);
                    const startXP = 100 * (L - 1) * L / 2;
                    combatRecords.prestigeLevel = L;
                    combatRecords.currentXP = lifetime - startXP;

                    tournamentHistory.push({
                        id: tournament.id, title: tournament.title, date: new Date().toISOString(),
                        kills: player.kills, xp: userXp, rank: player.position, prize: player.prize
                    });
                    if (tournamentHistory.length > 30) tournamentHistory.shift();

                    const eliminationBonus = player.actualKillEarnings || 0;
                    totalPrize = parseFloat(player.prize.toString().replace(/[^\d.-]/g, '')) || 0;

                    bountyVault.eliminationBonuses += eliminationBonus;
                    bountyVault.tournamentPayouts += totalPrize;
                    if (totalPrize > bountyVault.peakWinnings) bountyVault.peakWinnings = totalPrize;

                    batch.set(matchesRef, { userId: player.realUid, combatRecords, tournamentHistory, bountyVault }, { merge: true });
                    updatesCount++;
                }

                if (milestoneReward > 0 || totalPrize > 0) {
                    let eCoinsBalance = 0;
                    let topUpHistory = [];
                    if (vaultSnap.exists()) {
                        const vData = vaultSnap.data();
                        eCoinsBalance = vData.eCoinsBalance || 0;
                        topUpHistory = vData.topUpHistory || [];
                    }

                    const now = new Date();
                    const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).toUpperCase();
                    const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();

                    if (milestoneReward > 0) {
                        eCoinsBalance += milestoneReward;
                        topUpHistory.unshift({
                            id: 'TXN' + Math.floor(100000000000 + Math.random() * 900000000000),
                            amount: 'MILESTONE REWARD',
                            coins: milestoneReward,
                            date: `${dateStr} • ${timeStr}`,
                            status: 'COMPLETED',
                            timestamp: Date.now()
                        });
                    }

                    if (totalPrize > 0) {
                        eCoinsBalance += totalPrize;
                        topUpHistory.unshift({
                            id: 'TXN' + Math.floor(100000000000 + Math.random() * 900000000000),
                            amount: 'MATCH PRIZE',
                            coins: totalPrize,
                            date: `${dateStr} • ${timeStr}`,
                            status: 'COMPLETED',
                            timestamp: Date.now()
                        });
                    }

                    topUpHistory = topUpHistory.slice(0, 30);
                    batch.set(vaultRef, { eCoinsBalance, topUpHistory, userId: player.realUid }, { merge: true });
                    updatesCount++;
                }
            }

            // Always publish the match results to the match document
            const finalResultData = {
                teamResults,
                individualResults,
                perKillPrice,
                fixedValidKills,
                killPool,
                winnerPool,
                luckyNoobPool
            };
            const dailyMatchRef = doc(db, 'dailyMatches', tournament.docId, 'dates', tournament.dateStr || window.currentDateStr || new Date().toISOString().split('T')[0]);
            const dailyMatchSnap = await getDoc(dailyMatchRef);
            if (dailyMatchSnap.exists()) {
                const newMatches = (dailyMatchSnap.data().data || []).map(m => {
                    if (String(m.id) === String(tournament.id)) {
                        const totalSlots = m.slots ? String(m.slots).split(' / ')[1] : '100';
                        return { 
                            ...m, 
                            resultPublished: true, 
                            resultsData: finalResultData,
                            slots: `${individualResults.length} / ${totalSlots}`
                        };
                    }
                    return m;
                });
                batch.update(dailyMatchRef, { data: newMatches });
            }
            
            await batch.commit();
            
            if (updatesCount > 0) {
                alert(`Results published successfully! Updated ${updatesCount} player profiles.`);
            } else {
                alert("Results published to match successfully! (Note: No actual player profiles were updated).");
            }
            
            tournament.resultPublished = true;
        } catch (e) {
            console.error("Error publishing match stats:", e);
            alert("Error publishing results: " + e.message);
        } finally {
            setIsPublishing(false);
        }
    };


    const handleFindLuckyNoob = () => {
        if (isLuckyNoobFound) return;
        let eligiblePlayers = individualResults.filter(p => p.kills === 0);
        if (eligiblePlayers.length === 0) {
            eligiblePlayers = individualResults.filter(p => p.teamId !== 1);
        }
        if (eligiblePlayers.length > 0) {
            setSpinnerCandidates(eligiblePlayers);
            setCurrentSpinnerName(eligiblePlayers[0].name);
            setShowLuckySpinner(true);
            setIsSpinning(true);
        }
    };
    const [isApprovingReport, setIsApprovingReport] = useState(false);

    const handleApproveReport = async () => {
        if (!reportDetailsPlayer || isApprovingReport) return;
        setIsApprovingReport(true);
        try {
            const reportedUid = reportDetailsPlayer.uid;
            const usersRef = collection(db, 'users');
            const reportsToApprove = reportDetailsPlayer.reportedBy.filter(r => !r.status);
            if (reportsToApprove.length === 0) {
                setIsApprovingReport(false);
                return;
            }

            const updateConfirmedUser = async (userSnapshot) => {
                const userDoc = userSnapshot.docs[0];
                const data = userDoc.data();

                const currentConfirmedCount = data.confirmedReportsCount || data.profileData?.confirmedReportsCount || data.profileData?.confirmedReports || 0;
                const newConfirmedCount = currentConfirmedCount + 1;

                const currentReceivedCount = data.receivedReportsCount || data.profileData?.receivedReportsCount || data.profileData?.receivedReports || 0;
                const newReceivedCount = currentReceivedCount + reportsToApprove.length;

                const newTrustScore = Math.max(0, 100 - Math.floor(newConfirmedCount) * 20);

                const isNowBanned = newTrustScore <= 40;

                const getATS = (score) => {
                    if (score >= 100) return "IMPECCABLE";
                    if (score >= 80) return "RELIABLE";
                    if (score >= 60) return "FAIR";
                    if (score >= 40) return "QUESTIONABLE";
                    return "UNTRUSTED";
                };

                const updateData = {
                    'confirmedReportsCount': newConfirmedCount,
                    'profileData.confirmedReportsCount': newConfirmedCount,
                    'confirmedReports': newConfirmedCount,
                    'profileData.confirmedReports': newConfirmedCount,
                    'receivedReportsCount': newReceivedCount,
                    'profileData.receivedReportsCount': newReceivedCount,
                    'receivedReports': newReceivedCount,
                    'profileData.receivedReports': newReceivedCount,
                    'trustScore': newTrustScore,
                    'ats': getATS(newTrustScore),
                    'accountActive': !isNowBanned
                };

                if (isNowBanned && !data.isBanned) {
                    updateData.isBanned = true;
                    updateData.banReason = "Trust score dropped to 40 or below due to multiple confirmed reports.";
                    updateData.banDate = new Date().toISOString();
                }

                await updateDoc(doc(db, 'users', userDoc.id), updateData);
            };

            const q1 = query(usersRef, where('profileData.gameUid1', '==', String(reportedUid)));
            const snap1 = await getDocs(q1);
            if (!snap1.empty) {
                await updateConfirmedUser(snap1);
            } else {
                const q2 = query(usersRef, where('profileData.gameUid2', '==', String(reportedUid)));
                const snap2 = await getDocs(q2);
                if (!snap2.empty) {
                    await updateConfirmedUser(snap2);
                } else {
                    // Try one more time with Number in case it's saved as a number
                    const q1Num = query(usersRef, where('profileData.gameUid1', '==', Number(reportedUid)));
                    const snap1Num = await getDocs(q1Num);
                    if (!snap1Num.empty) await updateConfirmedUser(snap1Num);
                    else {
                        const q2Num = query(usersRef, where('profileData.gameUid2', '==', Number(reportedUid)));
                        const snap2Num = await getDocs(q2Num);
                        if (!snap2Num.empty) await updateConfirmedUser(snap2Num);
                        else console.log("User not found for reportedUid:", reportedUid);
                    }
                }
            }

            // Mark the match report documents as approved so it can't be approved again
            for (const r of reportsToApprove) {
                if (r.id) {
                    await updateDoc(doc(db, 'matchReports', String(tournament.id), 'reports', r.id), {
                        status: 'APPROVED'
                    });
                }
            }

            alert("Reports bulk approved and trust score degraded.");
            setReportDetailsPlayer(null);
        } catch (error) {
            console.error("Error approving report: ", error);
            alert("Failed to approve reports.");
        } finally {
            setIsApprovingReport(false);
        }
    };

    const handleRejectReport = async () => {
        if (!reportDetailsPlayer) return;
        const reportsToReject = reportDetailsPlayer.reportedBy.filter(r => !r.status);
        try {
            for (const r of reportsToReject) {
                if (r.id) {
                    await updateDoc(doc(db, 'matchReports', String(tournament.id), 'reports', r.id), {
                        status: 'REJECTED'
                    });
                }
            }
        } catch (e) {
            console.error("Error rejecting reports: ", e);
        }
        setReportDetailsPlayer(null);
    };

    const handleIndividualTick = async (reporter) => {
        if (!reporter || reporter.status) return;
        try {
            const reportedUid = reportDetailsPlayer.uid;
            const usersRef = collection(db, 'users');

            const updateUserReceived = async (userSnapshot) => {
                const userDoc = userSnapshot.docs[0];
                const data = userDoc.data();
                const currentReceivedCount = data.receivedReportsCount || data.profileData?.receivedReportsCount || data.profileData?.receivedReports || 0;
                const newReceivedCount = currentReceivedCount + 1;

                await updateDoc(doc(db, 'users', userDoc.id), {
                    'receivedReportsCount': newReceivedCount,
                    'profileData.receivedReportsCount': newReceivedCount,
                    'receivedReports': newReceivedCount,
                    'profileData.receivedReports': newReceivedCount
                });
            };

            const q1 = query(usersRef, where('profileData.gameUid1', '==', String(reportedUid)));
            const snap1 = await getDocs(q1);
            if (!snap1.empty) {
                await updateUserReceived(snap1);
            } else {
                const q2 = query(usersRef, where('profileData.gameUid2', '==', String(reportedUid)));
                const snap2 = await getDocs(q2);
                if (!snap2.empty) {
                    await updateUserReceived(snap2);
                } else {
                    const q1Num = query(usersRef, where('profileData.gameUid1', '==', Number(reportedUid)));
                    const snap1Num = await getDocs(q1Num);
                    if (!snap1Num.empty) await updateUserReceived(snap1Num);
                    else {
                        const q2Num = query(usersRef, where('profileData.gameUid2', '==', Number(reportedUid)));
                        const snap2Num = await getDocs(q2Num);
                        if (!snap2Num.empty) await updateUserReceived(snap2Num);
                        else console.log("User not found for individual tick:", reportedUid);
                    }
                }
            }

            if (reporter.id) {
                await updateDoc(doc(db, 'matchReports', String(tournament.id), 'reports', reporter.id), {
                    status: 'TICKED'
                });
                setReportDetailsPlayer(prev => ({
                    ...prev,
                    reportedBy: prev.reportedBy.map(r => r.id === reporter.id ? { ...r, status: 'TICKED' } : r)
                }));
            }
        } catch (error) {
            console.error("Error ticking individual report: ", error);
            alert("Failed to tick report.");
        }
    };

    const handleIndividualCross = async (reporter) => {
        if (!reporter || reporter.status) return;
        try {
            if (reporter.id) {
                await updateDoc(doc(db, 'matchReports', String(tournament.id), 'reports', reporter.id), {
                    status: 'CROSSED'
                });
                setReportDetailsPlayer(prev => ({
                    ...prev,
                    reportedBy: prev.reportedBy.map(r => r.id === reporter.id ? { ...r, status: 'CROSSED' } : r)
                }));
            }
        } catch (error) {
            console.error("Error crossing individual report: ", error);
        }
    };
    const userFoundInMatch = individualResults.some(p => gamerUser && (p.realUid === String(gamerUser.gameUid1) || p.realUid === String(gamerUser.gameUid2)));
    const finalIndividualResults = individualResults.map(p => {
        const matchesUser = gamerUser && (p.realUid === String(gamerUser.gameUid1) || p.realUid === String(gamerUser.gameUid2));
        const isHighlight = userFoundInMatch ? matchesUser : p.position === 1;
        
        if (isLuckyNoobFound && p.uid === luckyNoobPlayerUid && luckyNoobPool > 0) {
            const currentPrize = parseFloat(String(p.prize).replace(/[^\d.-]/g, '')) || 0;
            const newPrize = currentPrize + luckyNoobPool;
            return { ...p, prize: `₹ ${Number.isInteger(newPrize) ? newPrize : newPrize.toFixed(2)}`, isLucky: true, isHighlight };
        }
        return { ...p, isHighlight };
    }).sort((a, b) => a.position - b.position);

    const finalTeamResults = teamResults.map(t => {
        if (isLuckyNoobFound && luckyNoobPool > 0) {
            const luckyPlayer = individualResults.find(p => p.uid === luckyNoobPlayerUid);
            if (luckyPlayer && luckyPlayer.teamId === parseInt(t.teamName.replace('TEAM ', ''))) {
                const currentPrize = parseFloat(t.prize.replace(/[^\d.-]/g, '')) || 0;
                const newPrize = currentPrize + luckyNoobPool;
                return { ...t, prize: `₹ ${Number.isInteger(newPrize) ? newPrize : newPrize.toFixed(2)}`, isLucky: true };
            }
        }
        return t;
    });

    const displayData = viewType === 'TEAM' ? finalTeamResults : finalIndividualResults;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="w-full min-h-screen bg-transparent dark:bg-[#0a0a0f] font-['Monda'] uppercase text-gray-900 dark:text-white"
        >
            <style>{`
                /* Hide scrollbar globally when result page is active */
                html::-webkit-scrollbar,
                body::-webkit-scrollbar,
                *::-webkit-scrollbar {
                    display: none !important;
                }
                html, body, * {
                    -ms-overflow-style: none !important;  /* IE and Edge */
                    scrollbar-width: none !important;  /* Firefox */
                }
            `}</style>

            <div className="max-w-[120rem] mx-auto px-[5px] md:px-[15px] py-[5px] md:py-[15px] min-h-screen flex flex-col">
                {/* Header */}
                <div className="border-b border-gray-200 dark:border-white/10 pb-[5px] mb-[5px] md:pb-[15px] md:mb-[15px]">
                    <button onClick={onClose} className="text-gray-500 dark:text-gray-400 hover:text-pink-600 dark:hover:text-pink-500 mb-[5px] md:mb-4 hidden md:flex items-center gap-2 transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                        Back to Lobby
                    </button>

                    <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
                        <div className="w-full lg:w-auto text-center md:text-left">
                            <h2 className="text-3xl md:text-5xl font-black tracking-wider text-gray-900 dark:text-white leading-none mt-1">{tournament.title} <span className="text-pink-600 dark:text-pink-500">RESULTS</span></h2>
                            <div className="flex justify-center md:justify-start flex-wrap gap-[5px] md:gap-3 mt-[5px] md:mt-4">
                                {tournament.lobbyType?.toUpperCase() === 'HACKER VS HACKER' ? (
                                    <span className="bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 px-3 py-1 text-sm font-black tracking-widest rounded-none shadow-sm">HACKER LOBBY</span>
                                ) : (
                                    <span className="bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 px-3 py-1 text-sm font-black tracking-widest rounded-none shadow-sm">MOBILE LOBBY</span>
                                )}
                                <span className="bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 px-3 py-1 text-sm font-bold tracking-widest rounded-none uppercase">{mode}</span>
                                <span className="bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 px-3 py-1 text-sm font-bold tracking-widest rounded-none uppercase">{tournament.map}</span>
                            </div>
                        </div>

                        <div className="flex flex-col items-center lg:items-end gap-3 shrink-0 w-full lg:w-auto mt-[5px] md:mt-4 lg:mt-0">
                            {tournament?.isAdminView && (
                                <div className="flex gap-2">
                                    <input
                                        type="file"
                                        accept=".json"
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        onChange={handleFileUpload}
                                    />
                                    {!tournament.resultPublished && (
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            className="px-4 py-2 font-bold tracking-widest text-sm uppercase rounded-none transition-colors border bg-blue-600 hover:bg-blue-700 text-white border-blue-500 shadow-[0_0_15px_rgba(37,99,235,0.3)]"
                                        >
                                            UPLOAD RESULT
                                        </button>
                                    )}
                                    {(uploadedResult || tournament.resultPublished) && (
                                        <button
                                            onClick={handlePublishResults}
                                            disabled={isPublishing || tournament.resultPublished}
                                            className={`px-4 py-2 font-bold tracking-widest text-sm uppercase rounded-none transition-colors border ${tournament.resultPublished ? 'bg-gray-800 text-gray-500 border-gray-700 cursor-not-allowed' : 'bg-pink-600 hover:bg-pink-700 text-white border-pink-500 shadow-[0_0_15px_rgba(236,72,153,0.3)]'}`}
                                        >
                                            {isPublishing ? 'PUBLISHING...' : (tournament.resultPublished ? 'RESULTS PUBLISHED' : 'PUBLISH RESULTS')}
                                        </button>
                                    )}
                                </div>
                            )}
                            <button
                                onClick={() => setShowPrizeInfoModal(true)}
                                className="hidden lg:flex items-center justify-center p-2 bg-green-500/10 hover:bg-green-500/20 text-green-600 dark:text-green-400 border border-green-500/30 rounded-full transition-all hover:scale-105"
                                title="Prize Info"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            </button>

                            {isTeamMode && (
                                <div className="flex bg-gray-100 dark:bg-[#1a1a24] p-1.5 rounded-none border border-gray-200 dark:border-white/5 w-full lg:w-fit mt-1">
                                    <button
                                        onClick={() => setViewType('TEAM')}
                                        className={`flex-1 lg:flex-none px-2 lg:px-6 py-2.5 text-xs lg:text-sm font-bold tracking-wider transition-all duration-300 rounded-none ${viewType === 'TEAM'
                                            ? 'bg-pink-600 text-white shadow-[0_0_15px_rgba(236,72,153,0.3)]'
                                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-white/5'
                                            }`}
                                    >
                                        TEAM STATS
                                    </button>
                                    <button
                                        onClick={() => setViewType('INDIVIDUAL')}
                                        className={`flex-1 lg:flex-none px-2 lg:px-6 py-2.5 text-xs lg:text-sm font-bold tracking-wider transition-all duration-300 rounded-none ${viewType === 'INDIVIDUAL'
                                            ? 'bg-purple-600 text-white shadow-[0_0_15px_rgba(168,85,247,0.3)]'
                                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-white/5'
                                            }`}
                                    >
                                        INDIVIDUAL STATS
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {(!tournament.resultPublished && !uploadedResult) ? (
                    <div className="flex-1 flex flex-col items-center justify-center min-h-[50vh]">
                        <h3 className="text-2xl md:text-4xl font-black text-gray-700 dark:text-gray-300 uppercase tracking-widest text-center">MATCH RESULT NOT PUBLISHED YET</h3>
                        {tournament?.isAdminView && (
                            <p className="mt-4 text-gray-500 dark:text-gray-400 font-bold uppercase text-sm">Please upload the result JSON to preview and publish.</p>
                        )}
                    </div>
                ) : (
                    <>
                {/* Results Table */}
                <div className="flex-1 bg-white/80 dark:bg-[#1a1a24]/80 backdrop-blur-xl border border-gray-200 dark:border-white/5 rounded-none p-[5px] md:p-[15px] shadow-sm dark:shadow-none overflow-hidden flex flex-col">
                    <div className="overflow-x-auto overflow-y-auto pb-4 flex-1 custom-scrollbar">
                        <table className="w-full text-left min-w-[700px]">
                            <thead className="sticky top-0 bg-white dark:bg-[#1a1a24] z-10 shadow-sm">
                                <tr className="border-b border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 text-xs font-bold uppercase tracking-wider">
                                    {viewType === 'INDIVIDUAL' && (
                                        <th className="py-[15px] px-4 whitespace-nowrap text-center align-middle border-r border-gray-200 dark:border-white/10">Position</th>
                                    )}
                                    <th className={`py-[15px] px-4 whitespace-nowrap text-center align-middle border-r border-gray-200 dark:border-white/10`}>
                                        {viewType === 'INDIVIDUAL' ? 'Player' : 'Team Name'}
                                    </th>
                                    <th className={`py-[15px] px-4 whitespace-nowrap text-center align-middle border-r border-gray-200 dark:border-white/10`}>
                                        {viewType === 'INDIVIDUAL' ? 'Kills' : 'Total Eliminations'}
                                    </th>
                                    <th className={`py-[15px] px-4 whitespace-nowrap text-center text-purple-500 align-middle border-r border-gray-200 dark:border-white/10`}>
                                        {viewType === 'INDIVIDUAL' ? 'XP Earned' : 'Total XP Earned'}
                                    </th>
                                    <th className={`py-[15px] px-4 whitespace-nowrap text-center align-middle border-r border-gray-200 dark:border-white/10`}>
                                        {viewType === 'INDIVIDUAL' ? 'Prize' : 'Total Prize'}
                                    </th>
                                    {viewType === 'INDIVIDUAL' && tournament.isAdminView && tournament.lobbyType !== 'HACKER LOBBY' && tournament.lobbyType !== 'HACKER vs HACKER' && (
                                        <th className={`py-[15px] px-4 whitespace-nowrap text-center text-red-500 align-middle`}>
                                            Reports
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {displayData.map((r, i) => {
                                    const isHighlight = viewType === 'TEAM' ? r.isUserTeam : r.isHighlight;
                                    const isLucky = r.isLucky;
                                    return (
                                        <tr key={i} className={`border-b border-gray-200 dark:border-white/10 transition-colors ${isLucky ? 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30' : isHighlight ? 'bg-pink-50/80 dark:bg-pink-500/10 hover:bg-pink-100/80 dark:hover:bg-pink-500/20' : 'hover:bg-gray-50 dark:hover:bg-white/5'}`}>
                                            {viewType === 'INDIVIDUAL' && (
                                                <td className="py-4 px-4 relative text-center whitespace-nowrap border-r border-gray-200 dark:border-white/10">
                                                    {!isHighlight && tournament.lobbyType !== 'HACKER vs HACKER' && !tournament.isAdminView && !r.reportedBy.some(report => String(report.uid) === String(gamerUser?.uid || gamerUser?.gamerCode)) && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setReportPlayer(r);
                                                                setSelectedReasons({ wallHack: false, autoAim: false, speedHack: false });
                                                            }}
                                                            className="absolute top-0 left-0 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white text-[9px] font-black px-2 py-0.5 tracking-wider transition-all z-10 uppercase"
                                                            title="Report Gamer"
                                                        >
                                                            REPORT
                                                        </button>
                                                    )}
                                                    <div className="absolute top-0 right-0 bg-gray-200 dark:bg-[#2a2a36] text-gray-700 dark:text-gray-300 text-[9px] font-black px-2 py-0.5 tracking-wider">
                                                        TEAM {r.teamId}
                                                    </div>
                                                    <span className={`font-black text-lg ${r.posColor}`}>
                                                        #{r.position}
                                                    </span>
                                                </td>
                                            )}
                                            <td className="py-4 px-4 relative text-center font-bold text-gray-900 dark:text-white uppercase text-sm whitespace-nowrap group border-r border-gray-200 dark:border-white/10">
                                                {isLucky && (
                                                    <div className="absolute top-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[9px] font-black px-2 py-0.5 tracking-wider shadow-sm flex items-center gap-1 z-10">
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>
                                                        LUCKY NOOB
                                                    </div>
                                                )}
                                                {!isLucky && isHighlight && (
                                                    <div className="absolute top-0 right-0 bg-pink-600 dark:bg-pink-500 text-white text-[9px] font-black px-2 py-0.5 tracking-wider shadow-sm z-10">
                                                        YOU
                                                    </div>
                                                )}

                                                {viewType === 'INDIVIDUAL' ? (
                                                    <div className="flex flex-col items-center justify-center">
                                                        <span>{r.name}</span>
                                                        <span className="text-[10px] text-gray-500 dark:text-gray-400 font-normal tracking-widest mt-0.5">UID: {r.uid}</span>
                                                    </div>
                                                ) : (
                                                    <span className="flex items-center justify-center">
                                                        {r.teamName}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-4 px-4 text-center text-gray-600 dark:text-gray-300 font-bold uppercase text-sm whitespace-nowrap border-r border-gray-200 dark:border-white/10">{r.kills}</td>
                                            <td className="py-4 px-4 text-center text-purple-600 dark:text-purple-400 font-black uppercase text-sm whitespace-nowrap border-r border-gray-200 dark:border-white/10">
                                                {r.xp}
                                            </td>
                                            <td className={`py-4 px-4 text-center font-black text-green-600 dark:text-green-400 uppercase text-sm whitespace-nowrap ${viewType === 'INDIVIDUAL' && tournament.isAdminView && tournament.lobbyType !== 'HACKER LOBBY' && tournament.lobbyType !== 'HACKER vs HACKER' ? 'border-r border-gray-200 dark:border-white/10' : ''}`}>{r.prize}</td>
                                            {viewType === 'INDIVIDUAL' && tournament.isAdminView && tournament.lobbyType !== 'HACKER LOBBY' && tournament.lobbyType !== 'HACKER vs HACKER' && (
                                                <td className="py-4 px-4 text-center font-bold text-red-500 uppercase text-sm whitespace-nowrap relative group">
                                                    {r.reports > 0 && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setReportDetailsPlayer(r);
                                                            }}
                                                            className="absolute top-0 right-0 bg-blue-500/10 hover:bg-blue-500 text-blue-500 hover:text-white text-[9px] font-black px-2 py-0.5 tracking-wider transition-all z-10 uppercase"
                                                            title="View Report Details"
                                                        >
                                                            DETAILS
                                                        </button>
                                                    )}
                                                    {r.reports || 0}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                {displayData.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="text-center py-8 text-red-500 font-bold">
                                            DEBUG ERROR: NO DATA TO DISPLAY.<br/>
                                            Raw resultsData size: {tournament.resultsData ? JSON.stringify(tournament.resultsData).length : 'MISSING'}<br/>
                                            individualResults: {tournament.resultsData?.individualResults?.length || 0}<br/>
                                            teamResults: {tournament.resultsData?.teamResults?.length || 0}<br/>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                </>
                )}
            </div>

            <AnimatePresence>
                {showClaimModal && (
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-[15px] bg-black/80 backdrop-blur-sm"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="bg-white dark:bg-[#1a1a24] rounded-none border border-gray-200 dark:border-white/10 w-full max-w-md shadow-2xl overflow-hidden font-['Monda']"
                        >
                            <div className="bg-gradient-to-r from-pink-600 to-purple-600 p-[15px] flex items-center justify-center">
                                <div className="text-sm sm:text-base font-black text-white tracking-widest uppercase text-center w-full">
                                    SUBMIT YOUR STATS
                                </div>
                            </div>
                            <div className="p-[15px]">
                                <div className="mb-[15px] p-3 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 flex justify-between items-center rounded-none">
                                    <div>
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest mb-0.5">REGISTERED IGN</p>
                                        <p className="text-sm font-black text-pink-600 dark:text-pink-400 uppercase">{registeredIgn}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest mb-0.5">GAME UID</p>
                                        <p className="text-sm font-black text-gray-900 dark:text-white font-mono">{registeredUid}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-[15px] mb-[15px]">
                                    <div>
                                        <label className="block text-[11px] sm:text-xs font-bold text-gray-700 dark:text-gray-300 uppercase mb-2 truncate">1. Stats Screenshot</label>
                                        <label className="border-2 border-dashed border-gray-300 dark:border-white/20 rounded-none h-[54px] flex items-center justify-center hover:border-pink-500 transition-colors cursor-pointer group px-2 relative">
                                            <input
                                                type="file"
                                                className="hidden"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    if (e.target.files && e.target.files[0]) {
                                                        const file = e.target.files[0];
                                                        const url = URL.createObjectURL(file);
                                                        const img = new Image();
                                                        img.onload = () => {
                                                            if (img.width > img.height) {
                                                                setUploadedImage(file);
                                                                setPreviewUrl(url);
                                                            } else {
                                                                alert("Please upload a horizontal (landscape) screenshot. Vertical images are not accepted.");
                                                                URL.revokeObjectURL(url);
                                                            }
                                                        };
                                                        img.src = url;
                                                    }
                                                }}
                                            />
                                            {uploadedImage ? (
                                                <>
                                                    <div className="text-green-500 shrink-0">
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                                    </div>
                                                    <span className="ml-2 text-[10px] font-bold text-green-500 uppercase truncate">UPLOADED</span>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="text-gray-400 group-hover:text-pink-500 shrink-0">
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                                    </div>
                                                    <span className="ml-2 text-[10px] font-bold text-gray-500 group-hover:text-pink-500 transition-colors uppercase truncate">Upload Image</span>
                                                </>
                                            )}
                                        </label>
                                    </div>

                                    <div>
                                        <label className="block text-[11px] sm:text-xs font-bold text-gray-700 dark:text-gray-300 uppercase mb-2 truncate">2. Enter Your Kills</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={killsInput}
                                            onChange={(e) => {
                                                let val = e.target.value.replace(/\D/g, '');
                                                if (val !== '') {
                                                    if (parseInt(val) > 50) val = '50';
                                                }
                                                setKillsInput(val);
                                            }}
                                            className="w-full h-[54px] bg-gray-50 dark:bg-black/30 border border-gray-300 dark:border-white/10 rounded-none px-4 text-xl font-black text-gray-900 dark:text-white uppercase outline-none focus:border-pink-500 transition-colors text-center"
                                            onFocus={() => setIsFocused(true)}
                                            onBlur={() => setIsFocused(false)}
                                            placeholder={isFocused ? "" : "0"}
                                        />
                                    </div>
                                </div>

                                <div className="mb-[15px]">
                                    <div className="relative w-full aspect-video bg-gray-100 dark:bg-[#111] overflow-hidden border border-gray-200 dark:border-white/10 flex items-center justify-center">
                                        <img src={previewUrl || "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=800&q=80"} alt="Screenshot" className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${previewUrl ? 'opacity-100' : 'opacity-50'} ${isSubmitting ? 'blur-sm scale-105' : ''}`} />
                                        {!previewUrl && <span className="relative z-10 bg-black/60 text-white text-[10px] px-2 py-1 font-bold tracking-widest uppercase">EXAMPLE</span>}

                                        {/* AI Scanning Overlay */}
                                        <AnimatePresence>
                                            {isSubmitting && (
                                                <motion.div
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    exit={{ opacity: 0 }}
                                                    className="absolute inset-0 z-20 pointer-events-none bg-cyan-900/10 border border-cyan-500/30 overflow-hidden"
                                                >
                                                    {/* Scanning Line */}
                                                    <motion.div
                                                        animate={{ top: ["-20%", "100%"] }}
                                                        transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
                                                        className="absolute left-0 w-full flex flex-col"
                                                    >
                                                        <div className="h-24 w-full bg-gradient-to-b from-transparent via-cyan-500/10 to-cyan-400/30"></div>
                                                        <div className="h-[2px] w-full bg-cyan-400 shadow-[0_0_20px_#22d3ee,0_0_40px_#22d3ee]"></div>
                                                    </motion.div>

                                                    {/* Corner Brackets */}
                                                    <div className="absolute top-3 left-3 w-4 h-4 border-t-2 border-l-2 border-cyan-400"></div>
                                                    <div className="absolute top-3 right-3 w-4 h-4 border-t-2 border-r-2 border-cyan-400"></div>
                                                    <div className="absolute bottom-3 left-3 w-4 h-4 border-b-2 border-l-2 border-cyan-400"></div>
                                                    <div className="absolute bottom-3 right-3 w-4 h-4 border-b-2 border-r-2 border-cyan-400"></div>

                                                    {/* Central HUD / Crosshair */}
                                                    <div className="absolute inset-0 flex items-center justify-center opacity-60">
                                                        <div className="w-16 h-16 border border-cyan-400/50 rounded-full flex items-center justify-center relative animate-[spin_4s_linear_infinite]">
                                                            <div className="absolute w-[1px] h-3 bg-cyan-400 -top-1"></div>
                                                            <div className="absolute w-[1px] h-3 bg-cyan-400 -bottom-1"></div>
                                                            <div className="absolute w-3 h-[1px] bg-cyan-400 -left-1"></div>
                                                            <div className="absolute w-3 h-[1px] bg-cyan-400 -right-1"></div>
                                                        </div>
                                                        <div className="absolute w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse"></div>
                                                    </div>

                                                    {/* Scanning Text */}
                                                    <div className="absolute bottom-4 left-0 w-full text-center">
                                                        <span className="text-cyan-400 text-[9px] font-mono font-bold tracking-[0.3em] bg-black/60 px-2 py-1 rounded backdrop-blur-sm border border-cyan-500/30">ANALYZING STATS..</span>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>

                                <div className="flex gap-[15px] mt-[15px]">
                                    <button
                                        onClick={() => setShowClaimModal(false)}
                                        className="flex-1 py-3 bg-red-500/10 text-red-500 border border-red-500/20 font-black uppercase tracking-widest text-sm rounded-none hover:bg-red-500/20 transition-colors"
                                    >
                                        APPEAL
                                    </button>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={isSubmitting || !uploadedImage || killsInput === ''}
                                        className={`flex-[2] py-3 font-black uppercase tracking-widest text-sm rounded-none transition-all flex items-center justify-center gap-2 ${(isSubmitting || !uploadedImage || killsInput === '') ? 'bg-gray-200 dark:bg-white/5 text-gray-400 dark:text-gray-600 cursor-not-allowed shadow-none' : 'bg-pink-600 hover:bg-pink-700 text-white shadow-[0_0_20px_rgba(236,72,153,0.3)]'}`}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                HOLD ON...
                                            </>
                                        ) : 'SUBMIT'}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Success Modal */}
            <AnimatePresence>
                {showSuccessModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white dark:bg-[#1a1a24] rounded-2xl w-full max-w-lg p-6 sm:p-8 text-left border border-gray-200 dark:border-white/10 shadow-2xl font-['Monda'] relative overflow-hidden flex flex-col max-h-[90vh]"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-wider">EXTRACTION RESULT</h3>
                                <div className="w-8 h-8 bg-green-100 dark:bg-green-500/20 rounded-full flex items-center justify-center relative">
                                    <svg className="w-4 h-4 text-green-500 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            </div>

                            <p className="text-gray-500 dark:text-gray-400 text-xs font-bold uppercase mb-4">
                                Gemini AI parsed the following data:
                            </p>

                            <div className="flex-1 overflow-auto bg-gray-50 dark:bg-black/30 border border-gray-200 dark:border-white/10 p-4 mb-6 rounded text-left">
                                <pre className="text-[10px] sm:text-xs text-gray-800 dark:text-gray-300 font-mono whitespace-pre-wrap break-words">
                                    {parsedJsonData || "No data"}
                                </pre>
                            </div>

                            <button
                                onClick={() => setShowSuccessModal(false)}
                                className="w-full py-3 bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white font-black uppercase tracking-widest text-sm rounded-xl hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                            >
                                CLOSE
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Error Modal */}
            <AnimatePresence>
                {showErrorModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white dark:bg-[#1a1a24] rounded-2xl w-full max-w-sm p-8 text-center border border-red-500/30 shadow-[0_0_30px_rgba(239,68,68,0.2)] font-['Monda'] relative overflow-hidden"
                        >
                            <div className="w-20 h-20 bg-red-100 dark:bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                                <div className="absolute -inset-2 bg-red-500/10 rounded-full animate-pulse"></div>
                                <svg className="w-10 h-10 text-red-500 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>

                            <h3 className="text-xl font-black text-red-600 dark:text-red-500 uppercase tracking-wider mb-2">REGISTRATION ERROR</h3>
                            <p className="text-gray-500 dark:text-gray-400 text-xs font-bold uppercase mb-8">
                                THE UPLOADED SCREENSHOT DOES NOT CONTAIN THE DATABASE FOR THIS MATCH.
                            </p>

                            <button
                                onClick={() => {
                                    setShowErrorModal(false);
                                    onClose(); // Kick out to lobby
                                }}
                                className="w-full py-4 bg-red-600 text-white font-black uppercase tracking-widest text-sm rounded-xl hover:bg-red-700 transition-colors shadow-lg shadow-red-500/30"
                            >
                                CLOSE & EXIT
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Report Modal */}
            <AnimatePresence>
                {reportPlayer && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="bg-[#1a1a24] rounded-xl w-full max-w-sm border border-white/10 shadow-2xl font-['Monda'] relative overflow-hidden"
                        >
                            <div className="p-4 border-b border-white/10 text-center">
                                <h3 className="text-xl font-black text-white uppercase tracking-wider">REPORT GAMER</h3>
                                <p className="text-[10px] sm:text-xs text-red-400 mt-1 uppercase font-bold">{reportPlayer.name} (UID: {reportPlayer.uid})</p>
                            </div>

                            <div className="p-6">
                                <p className="text-xs text-gray-400 font-bold uppercase mb-4">SELECT REASON</p>
                                <div className="flex flex-col gap-4 mb-8">
                                    <label className="flex items-center gap-4 cursor-pointer group">
                                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${selectedReasons.autoAim ? 'bg-red-600 border-red-600' : 'bg-black/30 border-white/20 group-hover:border-red-500/50'}`}>
                                            {selectedReasons.autoAim && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>}
                                        </div>
                                        <span className={`text-sm font-black uppercase tracking-wider transition-colors ${selectedReasons.autoAim ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`}>AUTO AIM</span>
                                        <input type="checkbox" className="hidden" checked={selectedReasons.autoAim} onChange={(e) => setSelectedReasons(prev => ({ ...prev, autoAim: e.target.checked }))} />
                                    </label>
                                    <label className="flex items-center gap-4 cursor-pointer group">
                                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${selectedReasons.wallHack ? 'bg-red-600 border-red-600' : 'bg-black/30 border-white/20 group-hover:border-red-500/50'}`}>
                                            {selectedReasons.wallHack && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>}
                                        </div>
                                        <span className={`text-sm font-black uppercase tracking-wider transition-colors ${selectedReasons.wallHack ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`}>WALL HACK</span>
                                        <input type="checkbox" className="hidden" checked={selectedReasons.wallHack} onChange={(e) => setSelectedReasons(prev => ({ ...prev, wallHack: e.target.checked }))} />
                                    </label>
                                    <label className="flex items-center gap-4 cursor-pointer group">
                                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${selectedReasons.speedHack ? 'bg-red-600 border-red-600' : 'bg-black/30 border-white/20 group-hover:border-red-500/50'}`}>
                                            {selectedReasons.speedHack && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>}
                                        </div>
                                        <span className={`text-sm font-black uppercase tracking-wider transition-colors ${selectedReasons.speedHack ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`}>SPEED HACK</span>
                                        <input type="checkbox" className="hidden" checked={selectedReasons.speedHack} onChange={(e) => setSelectedReasons(prev => ({ ...prev, speedHack: e.target.checked }))} />
                                    </label>
                                </div>

                                <div className="flex gap-3 mt-4">
                                    <button
                                        onClick={() => setReportPlayer(null)}
                                        className="flex-1 py-3 bg-white/5 text-white font-black uppercase tracking-widest text-sm rounded-md hover:bg-white/10 transition-colors"
                                    >
                                        CANCEL
                                    </button>
                                    <button
                                        onClick={async () => {
                                            const reportedUid = reportPlayer.uid;
                                            const reporterUserId = gamerUser?.uid || gamerUser?.gamerCode;
                                            const isCleanLobby = tournament?.lobbyType === 'CLEAN LOBBY';

                                            if (isCleanLobby && reporterUserId) {
                                                try {
                                                    // Increment filedReportsCount for current user
                                                    const reporterRef = gamerUser.uid ? doc(db, 'users', reporterUserId) : doc(db, 'gameswap', 'data', 'gamers', reporterUserId);
                                                    await updateDoc(reporterRef, {
                                                        'filedReportsCount': increment(1),
                                                        'profileData.filedReportsCount': increment(1)
                                                    });

                                                    // No longer updating the reported user's receivedReportsCount here.
                                                    // It will be updated by the admin in the Report Details modal.

                                                    // Save actual report for this match
                                                    if (tournament?.id) {
                                                        const reasons = Object.entries(selectedReasons).filter(([k, v]) => v).map(([k]) => k.toUpperCase().replace('HACK', ' HACK').replace('AUTOAIM', 'AUTO AIM'));
                                                        const newReport = {
                                                            name: gamerUser?.profileData?.name || gamerUser?.name || "GAMER",
                                                            uid: reporterUserId,
                                                            playerId: gamerUser?.playerId || gamerUser?.profileData?.playerId || "N/A",
                                                            reporterGameUid: gamerUser?.profileData?.primaryUidSlot === 2 ? (gamerUser?.profileData?.gameUid2 || "") : (gamerUser?.profileData?.gameUid1 || ""),
                                                            reportedUid: reportedUid,
                                                            reasons: reasons
                                                        };
                                                        await addDoc(collection(db, 'matchReports', String(tournament.id), 'reports'), newReport);
                                                        setRealReportsData(prev => [...prev, newReport]);
                                                    }
                                                } catch (err) {
                                                    console.error("Error submitting report:", err);
                                                }
                                            }

                                            setReportPlayer(null);
                                            setReportSubmitted(true);
                                            setTimeout(() => setReportSubmitted(false), 3000);
                                        }}
                                        disabled={!selectedReasons.wallHack && !selectedReasons.autoAim && !selectedReasons.speedHack}
                                        className="flex-1 py-3 bg-red-600/80 hover:bg-red-600 disabled:bg-white/5 disabled:text-gray-600 disabled:shadow-none text-white font-black uppercase tracking-widest text-sm rounded-md transition-colors shadow-[0_0_15px_rgba(220,38,38,0.2)] hover:shadow-[0_0_20px_rgba(220,38,38,0.4)]"
                                    >
                                        SUBMIT
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Report Submitted Modal */}
            <AnimatePresence>
                {reportSubmitted && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="bg-[#1a1a24] rounded-xl w-full max-w-sm border border-white/10 shadow-2xl font-['Monda'] relative overflow-hidden text-center"
                        >
                            <div className="p-4 border-b border-white/10">
                                <h3 className="text-xl font-black text-white uppercase tracking-wider">REPORT GAMER</h3>
                            </div>

                            <div className="p-8">
                                <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h4 className="text-lg font-black text-white uppercase tracking-wider mb-2">REPORT SUBMITTED</h4>
                                <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">
                                    WE WILL REVIEW THIS PLAYER.
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Lucky Spinner Modal */}
            <AnimatePresence>
                {showLuckySpinner && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl"
                    >
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0, y: 50 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.8, opacity: 0, y: 50 }}
                            transition={{ type: "spring", damping: 15, stiffness: 200 }}
                            className="bg-gradient-to-b from-[#1a1a24] to-black border border-amber-500/30 rounded-none w-full max-w-md p-1 font-['Monda'] relative overflow-hidden flex flex-col items-center"
                        >
                            {/* Tech Borders */}
                            <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-amber-500 z-10"></div>
                            <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-amber-500 z-10"></div>
                            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-amber-500 z-10"></div>
                            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-amber-500 z-10"></div>

                            <div className="w-full bg-[#0a0a0f] p-8 relative overflow-hidden flex flex-col items-center border border-white/5">
                                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20 pointer-events-none"></div>
                                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent"></div>

                                {/* Animated scanline */}
                                <motion.div
                                    animate={{ top: ['-20%', '120%'] }}
                                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                                    className="absolute left-0 w-full h-20 bg-gradient-to-b from-transparent via-amber-500/10 to-transparent pointer-events-none"
                                />

                                <h3 className="text-sm md:text-base font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500 uppercase tracking-[0.3em] mb-8 text-center">
                                    {isSpinning ? "SYSTEM OVERRIDE: SELECTING NOOB..." : "LUCKY NOOB DETECTED!"}
                                </h3>

                                <div className="w-full bg-black/80 border border-amber-500/40 py-8 px-4 relative overflow-hidden mb-8 flex justify-center items-center h-32">
                                    {/* Crosshairs inside box */}
                                    <div className="absolute top-2 left-2 w-3 h-3 border-t border-l border-amber-500/50"></div>
                                    <div className="absolute top-2 right-2 w-3 h-3 border-t border-r border-amber-500/50"></div>
                                    <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-amber-500/50"></div>
                                    <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-amber-500/50"></div>

                                    {isSpinning && (
                                        <motion.div
                                            animate={{ opacity: [0.1, 0.5, 0.1] }}
                                            transition={{ duration: 0.2, repeat: Infinity }}
                                            className="absolute inset-0 bg-amber-500/10 pointer-events-none"
                                        />
                                    )}
                                    <motion.span
                                        key={currentSpinnerName}
                                        initial={isSpinning ? { y: 20, opacity: 0 } : { scale: 0.5, opacity: 0 }}
                                        animate={isSpinning ? { y: 0, opacity: 1 } : { scale: 1, opacity: 1 }}
                                        transition={{ duration: isSpinning ? 0.05 : 0.5, type: isSpinning ? "tween" : "spring" }}
                                        className={`text-xl md:text-2xl font-black uppercase tracking-widest text-center whitespace-nowrap ${isSpinning ? 'text-gray-400 blur-[0.5px]' : 'text-amber-400'}`}
                                    >
                                        {currentSpinnerName}
                                    </motion.span>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500/50"></div>
                                    <p className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-[0.2em] whitespace-nowrap">
                                        TOTAL REWARD: <span className="text-amber-500 font-black text-sm md:text-base">₹ {luckyNoobPool.toFixed(2)}</span>
                                    </p>
                                    <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500/50"></div>
                                </div>

                                {!isSpinning && tempLuckyNoobUid && (
                                    <div className="flex gap-4 w-full mt-6">
                                        <button onClick={handleRechooseLuckyNoob} className="flex-1 py-3 bg-red-600/10 hover:bg-red-600/30 border border-red-500/40 text-red-500 font-black uppercase tracking-widest text-xs transition-colors rounded-none">
                                            RECHOOSE
                                        </button>
                                        <button onClick={handleConfirmLuckyNoob} className="flex-1 py-3 bg-green-600/10 hover:bg-green-600/30 border border-green-500/40 text-green-500 font-black uppercase tracking-widest text-xs transition-colors rounded-none">
                                            CONFIRM
                                        </button>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showPrizeInfoModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                        onClick={() => setShowPrizeInfoModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-gray-50 dark:bg-[#1a1a24] rounded-xl w-full max-w-sm border border-green-200 dark:border-green-500/20 shadow-2xl font-['Monda'] relative overflow-hidden text-center"
                        >
                            <div className="p-4 border-b border-green-200 dark:border-green-500/20 bg-green-50 dark:bg-green-900/10 flex justify-between items-center">
                                <h3 className="text-lg font-black text-green-800 dark:text-green-400 uppercase tracking-wider">PRIZE CALCULATION</h3>
                                <button onClick={() => setShowPrizeInfoModal(false)} className="text-green-600 hover:text-green-800 dark:text-green-500 dark:hover:text-green-300 transition-colors">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>

                            <div className="p-6 text-left flex flex-col gap-4">
                                <div className="flex justify-between items-center border-b border-gray-200 dark:border-white/10 pb-2">
                                    <span className="text-xs font-bold text-gray-500 uppercase">WINNER POOL</span>
                                    <span className="text-sm font-black text-gray-900 dark:text-white">₹ {winnerPool}</span>
                                </div>
                                <div className="flex justify-between items-center border-b border-gray-200 dark:border-white/10 pb-2">
                                    <span className="text-xs font-bold text-gray-500 uppercase">KILL POOL</span>
                                    <span className="text-sm font-black text-gray-900 dark:text-white">₹ {killPool}</span>
                                </div>
                                <div className="flex justify-between items-center border-b border-gray-200 dark:border-white/10 pb-2">
                                    <span className="text-xs font-bold text-gray-500 uppercase">VALID KILLS</span>
                                    <span className="text-sm font-black text-gray-900 dark:text-white">{fixedValidKills}</span>
                                </div>
                                <div className="flex justify-between items-center border-b border-green-200 dark:border-green-500/20 pb-2">
                                    <span className="text-xs font-bold text-green-600 dark:text-green-500 uppercase">PER KILL (₹ {killPool} / {fixedValidKills})</span>
                                    <span className="text-sm font-black text-green-700 dark:text-green-400">₹ {perKillPrice.toFixed(2)}</span>
                                </div>

                                {luckyNoobPool > 0 && (
                                    <div className="bg-green-100 dark:bg-green-900/20 p-4 rounded-lg mt-2 flex flex-col gap-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs font-bold text-green-700 dark:text-green-500 uppercase">LUCKY NOOB POOL</span>
                                            <span className="text-sm font-black text-green-800 dark:text-green-300">₹ {luckyNoobPool.toFixed(2)}</span>
                                        </div>
                                        {!isLuckyNoobFound ? (
                                            <button onClick={() => {
                                                setShowPrizeInfoModal(false);
                                                handleFindLuckyNoob();
                                            }} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs py-2.5 font-black uppercase tracking-widest shadow-md transition-colors rounded">
                                                FIND A LUCKY NOOB
                                            </button>
                                        ) : (
                                            <div className="w-full text-center bg-amber-100 text-amber-700 py-2 text-xs font-black uppercase rounded">LUCKY NOOB FOUND!</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Report Details Modal */}
            <AnimatePresence>
                {reportDetailsPlayer && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
                        onClick={() => setReportDetailsPlayer(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="bg-[#1a1a24] rounded-xl w-full max-w-md border border-[#2a2a35] shadow-2xl font-['Monda'] relative overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-start p-4 border-b border-[#2a2a35] bg-[#1a1a24]">
                                <div>
                                    <h3 className="text-xl font-black text-white uppercase tracking-wider">REPORT DETAILS</h3>
                                    <p className="text-[10px] sm:text-xs text-red-500 mt-1 uppercase font-bold">{reportDetailsPlayer.name} (UID: {reportDetailsPlayer.uid})</p>
                                </div>
                                {tournament?.isAdminView && reportDetailsPlayer.reportedBy?.length >= 5 ? (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={handleRejectReport}
                                            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs font-black uppercase rounded shadow transition-colors"
                                        >
                                            REJECT
                                        </button>
                                        <button
                                            onClick={handleApproveReport}
                                            disabled={isApprovingReport}
                                            className={`px-3 py-1.5 text-white text-xs font-black uppercase rounded shadow transition-colors ${isApprovingReport ? 'bg-red-500/50 cursor-not-allowed' : 'bg-red-600 hover:bg-red-500'}`}
                                        >
                                            {isApprovingReport ? 'APPROVING...' : 'APPROVE'}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => setReportDetailsPlayer(null)}
                                        className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                )}
                            </div>

                            <div className="p-0 max-h-[60vh] overflow-y-auto custom-scrollbar">
                                {reportDetailsPlayer.reportedBy && reportDetailsPlayer.reportedBy.length > 0 ? (
                                    <div className="flex flex-col">
                                        {reportDetailsPlayer.reportedBy.map((reporter, idx) => (
                                            <div key={idx} className="border-b border-[#2a2a35] last:border-b-0 p-4 flex flex-col gap-2 relative">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-black text-white uppercase truncate">{reporter.name}</span>
                                                    <div className="flex flex-col items-end mr-[5.5rem]">
                                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{reporter.playerId || "N/A"}</span>
                                                        <span className="text-[9px] text-gray-500 font-bold uppercase tracking-wider mt-0.5">UID: {reporter.reporterGameUid || "N/A"}</span>
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap gap-2 pr-[5.5rem]">
                                                    {(reporter.reasons || [reporter.reason]).map((reason, ridx) => (
                                                        <div key={ridx} className="bg-red-500/10 text-red-500 border border-red-500/20 px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest whitespace-nowrap">
                                                            {reason}
                                                        </div>
                                                    ))}
                                                </div>
                                                {tournament?.isAdminView && reportDetailsPlayer.reportedBy?.length < 5 && (
                                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pl-4 border-l border-[#2a2a35] h-[80%]">
                                                        <button
                                                            onClick={() => handleIndividualTick(reporter)}
                                                            className={`p-1.5 rounded-full bg-green-500/10 text-green-500 hover:bg-green-500/20 ${reporter.status ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            disabled={!!reporter.status}
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                                                        </button>
                                                        <button
                                                            onClick={() => handleIndividualCross(reporter)}
                                                            className={`p-1.5 rounded-full bg-red-500/10 text-red-500 hover:bg-red-500/20 ${reporter.status ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            disabled={!!reporter.status}
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500 font-bold uppercase tracking-widest text-sm">
                                        No reports found.
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
